import {closeDialog, openDeleteDialog, openDialog} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';

import {isNil} from 'ramda';
import {isArray, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useDeleteServiceOrderItemsMutation,
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseOrdersQuery,
  usePostServiceItemsMaterialPartRequestCancellationMutation,
  usePostServiceItemsMaterialPartRequestMutation,
  usePostServiceItemsMaterialPartReturnMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, catchUnhandledDataGridActions, DmsDataGrid, queryParams} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {BulkAssignMechanicForm} from '../../BulkAssignMechanicForm';
import {BulkDiscountForm} from '../../BulkDiscountForm';
import {GoodwillForm} from '../../Goodwill/GoodwillForm';
import {IssueMaterialItems} from '../../IssueMaterialItems';
import {MoveRequestItem} from '../../MoveRequestItem/MoveRequestItem';
import {
  RequestEditDialogProps,
  RequestEditDialog,
  ReceiveJobItemRowData,
} from '../../RequestEditDialog';
import {WorkSplittingForm} from '../../WorkSplitting/WorkSplittingForm';

const PREDEFINED_COLUMNS = [
  'itemName',
  'itemType',
  'itemPriceType',
  'itemGoodwillRatio',
  'serviceOrderJobItemId',
  'serviceJobId',
  'warehouseArticleId',
  'assignMechanics',
  'itemUnitPriceWithVat',
  'itemUnitPriceWithoutVat',
  'itemUnit',
  'itemQuantity',
] as const;

interface JobItemsDataGridProps extends TestIdProps {
  serviceCaseId: string;
  orderId: string | Nullish;
  jobId: string | Nullish;
  datagridKey: string | Nullish;
  requestName: string | Nullish;
}

export function JobItemsDataGrid(props: JobItemsDataGridProps) {
  const [, setServiceOrderReturnId] = useQueryState(queryParams.SERVICE_ORDER_RETURN_ID);
  const [, setServiceOrderReturnAuthorizationProfileId] = useQueryState(
    queryParams.SERVICE_ORDER_RETURN_AUTHORIZATION_PROFILE_ID
  );

  const [deleteServiceOrderItems] = useDeleteServiceOrderItemsMutation();
  const [postServiceItemsMaterialPartRequest] = usePostServiceItemsMaterialPartRequestMutation();
  const [postServiceItemsMaterialPartReturn] = usePostServiceItemsMaterialPartReturnMutation();
  const [postServiceItemsMaterialPartRequestCancellation] =
    usePostServiceItemsMaterialPartRequestCancellationMutation();

  const [editDialogProps, setEditDialogProps] = useState<RequestEditDialogProps | null>(null);

  const serviceCaseQuery = useGetMetadaServiceCaseQuery({
    serviceCaseId: props.serviceCaseId,
  });
  const serviceCaseOrdersQuery = useGetServiceCaseOrdersQuery({
    serviceCaseId: props.serviceCaseId,
  });

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => ({
      ...filter,
      serviceJobId: props.jobId ?? '',
      serviceOrderId: props.orderId ?? '',
      serviceCaseId: props.serviceCaseId,
    }),
    [props.jobId, props.orderId, props.serviceCaseId]
  );

  const actionCallback: ActionCallback = async ({
    rowId,
    actionKey,
    rowData,
    refreshData,
    queryId,
    deselectAll,
  }) => {
    const castedRowData = rowData as ReceiveJobItemRowData | ReceiveJobItemRowData[];

    const items = isArray(castedRowData)
      ? castedRowData.map((selectedRow) => ({
          serviceItemId: selectedRow.serviceOrderJobItemId.value,
          serviceJobId: props.jobId || '',
        }))
      : [
          {
            serviceItemId: castedRowData.serviceOrderJobItemId.value,
            serviceJobId: props.jobId || '',
          },
        ];

    const hasSomeGoodwill =
      isArray(castedRowData) &&
      castedRowData.some(
        (data) =>
          isNotNilOrEmpty(data.itemGoodwillRatio.value) &&
          Number(data.itemGoodwillRatio.value?.label) !== 100
      );

    await match(actionKey)
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteServiceOrderItems({
              serviceOrderId: props.orderId ?? '',
              serviceCaseId: props.serviceCaseId,
              body: {items},
            })
              .unwrap()
              .then(() => {
                deselectAll();
                refreshData();
              })
              .catch(handleApiError),
        })
      )
      .with('edit', 'redirectDetail', () => {
        if (isArray(castedRowData) || isNil(castedRowData)) {
          return;
        }

        const handleClose = () => {
          setEditDialogProps(null);
          refreshData();
        };

        setEditDialogProps({
          gridCode: 'service-case-order-job-items',
          rowId: isArray(rowId) ? rowId[0] : rowId,
          dataQueryId: queryId,
          serviceCaseId: props.serviceCaseId,
          orderId: props.orderId ?? '',
          itemData: castedRowData,
          onClose: handleClose,
          'data-testid': props['data-testid'] as string,
        });
      })
      .with('move', () =>
        openDialog(
          <MoveRequestItem
            onClose={() => closeDialog('moveRequestItem')}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={String(props.orderId)}
            hiddenServiceJob={props.jobId}
            items={items}
            onMoveItems={refreshData}
            data-testid={suffixTestId('moveItem', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.moveRequestItem'),
            id: 'moveRequestItem',
          }
        )
      )
      .with('goodwill', () =>
        openDialog(
          <GoodwillForm
            onClose={() => closeDialog('goodwill')}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={String(props.orderId)}
            requestName={props.requestName}
            items={items}
            onSaved={refreshData}
            isWithMultipleGoodwill={hasSomeGoodwill}
            data-testid={suffixTestId('goodwillDialog', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.goodwill'),
            id: 'goodwill',
          }
        )
      )
      .with('workSplitting', () =>
        openDialog(
          <WorkSplittingForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId ?? ''}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('workSplitting')}
            data-testid={suffixTestId('workSplitting', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.workSplitting'),
            id: 'workSplitting',
          }
        )
      )
      .with(
        'request',
        async () =>
          await postServiceItemsMaterialPartRequest({
            body: {serviceItems: items},
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.orderId ?? '',
          })
            .unwrap()
            .then(() => refreshData())
            .catch(handleApiError)
      )
      .with('issue', () => {
        const defaultMechanicId = isArray(castedRowData)
          ? castedRowData[0]?.assignMechanics?.value?.[0]?.key
          : castedRowData?.assignMechanics?.value?.[0]?.key;

        return openDialog(
          <IssueMaterialItems
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId ?? ''}
            items={items}
            defaultMechanicId={defaultMechanicId}
            onSubmit={() => refreshData()}
            onClose={() => closeDialog('issueMaterialItem')}
            data-testid={suffixTestId('issueMaterialItem', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.issueMaterialItem'),
            id: 'issueMaterialItem',
            size: 'small',
          }
        );
      })
      .with('assignMechanic', () =>
        openDialog(
          <BulkAssignMechanicForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId ?? ''}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('assignMechanic')}
            data-testid={suffixTestId('assignMechanic', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.assignMechanic'),
            id: 'assignMechanic',
          }
        )
      )
      .with('discount', () =>
        openDialog(
          <BulkDiscountForm
            serviceCaseId={props.serviceCaseId ?? ''}
            serviceOrderId={props.orderId ?? ''}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('manualDiscount')}
            data-testid={suffixTestId('manualDiscount', props)}
          />,
          {
            title: i18n.t('entity.orderItem.labels.manualDiscount'),
            id: 'manualDiscount',
            size: 'small',
          }
        )
      )
      .with('return', () => {
        if (isNil(props.orderId)) {
          throw new Error('serviceOrderId is not defined');
        }

        postServiceItemsMaterialPartReturn({
          body: {serviceItems: items},
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId,
        })
          .unwrap()
          .then((result) => {
            if (isNotNil(result?.serviceOrderReturnId)) {
              setServiceOrderReturnId(result?.serviceOrderReturnId);
            }
            if (isNotNil(result?.authorizationProfileId)) {
              setServiceOrderReturnAuthorizationProfileId(result?.authorizationProfileId);
            }
          })
          .then(deselectAll)
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('requestCancellation', () =>
        postServiceItemsMaterialPartRequestCancellation({
          body: {serviceItems: items},
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId ?? '',
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Show when={isNotNil(editDialogProps)}>
        <RequestEditDialog {...(editDialogProps as RequestEditDialogProps)} />
      </Show>
      <DmsDataGrid
        autoHeight
        predefinedColumns={PREDEFINED_COLUMNS}
        gridCode="service-case-order-job-items"
        queryModifier={queryModifier}
        actionCallback={actionCallback}
        data-testid={suffixTestId('dataGrid', props)}
      />
    </>
  );
}
